/**
 * Receipt and document printing.
 *
 * Three routes, picked in order of what the device actually offers:
 *
 *   1. Android (Capacitor) with a Bluetooth thermal printer paired — the ESC/POS
 *      bytes the server produced go straight to the device.
 *   2. A browser with WebUSB and a USB thermal printer — same bytes, over USB.
 *   3. Anything else — a print window laid out to the roll width, which also
 *      covers A4 for an office printer and "save as PDF" for sending a copy.
 *
 * The bytes are produced once, on the server, so a receipt reprinted from a
 * phone is byte-identical to the one the counter printed.
 */

import type { DailyReport, DeliveryRouteDay, DeliveryStop, StockHistory } from '@bakery/shared';
import { escapeHtml } from '@bakery/shared';

import type { ReceiptPayload } from './api.js';

/** True when running inside the Capacitor Android shell rather than a browser tab. */
export function isNativeAndroid(): boolean {
  return typeof window !== 'undefined' && Boolean((window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.());
}

interface BluetoothPrinterPlugin {
  list(): Promise<{ devices: { name: string; address: string }[] }>;
  connect(options: { address: string }): Promise<void>;
  write(options: { data: string }): Promise<void>;
}

function bluetoothPlugin(): BluetoothPrinterPlugin | null {
  const capacitor = (window as { Capacitor?: { Plugins?: Record<string, unknown> } }).Capacitor;
  return (capacitor?.Plugins?.ThermalPrinter as BluetoothPrinterPlugin | undefined) ?? null;
}

export interface PrintResult {
  ok: boolean;
  route: 'bluetooth' | 'usb' | 'window';
  error?: string;
}

export async function printReceipt(receipt: ReceiptPayload): Promise<PrintResult> {
  if (isNativeAndroid()) {
    const plugin = bluetoothPlugin();
    const address = localStorage.getItem('bakery.printer.address');
    if (plugin && address) {
      try {
        await plugin.connect({ address });
        await plugin.write({ data: receipt.escpos });
        return { ok: true, route: 'bluetooth' };
      } catch (error) {
        // Fall through to the print window rather than losing the sale's receipt.
        console.warn('Bluetooth print failed, falling back to a print window', error);
      }
    }
  }

  const usb = await tryWebUsb(receipt);
  if (usb) return usb;

  openPrintWindow(receiptDocument(receipt), `Receipt ${receipt.receiptNo}`);
  return { ok: true, route: 'window' };
}

/** WebUSB is Chromium-only and needs a user gesture; failure here is expected. */
async function tryWebUsb(receipt: ReceiptPayload): Promise<PrintResult | null> {
  const usb = (navigator as Navigator & { usb?: { getDevices(): Promise<USBDeviceLike[]> } }).usb;
  if (!usb) return null;

  try {
    const devices = await usb.getDevices();
    const printer = devices[0];
    if (!printer) return null;

    await printer.open();
    if (printer.configuration === null) await printer.selectConfiguration(1);
    await printer.claimInterface(0);

    const bytes = Uint8Array.from(atob(receipt.escpos), (character) => character.charCodeAt(0));
    await printer.transferOut(1, bytes);
    return { ok: true, route: 'usb' };
  } catch (error) {
    console.warn('USB print unavailable', error);
    return null;
  }
}

interface USBDeviceLike {
  open(): Promise<void>;
  configuration: unknown;
  selectConfiguration(value: number): Promise<void>;
  claimInterface(value: number): Promise<void>;
  transferOut(endpoint: number, data: Uint8Array): Promise<unknown>;
}

/**
 * The receipt as a printed document rather than a wall of teleprinter text.
 *
 * What this replaced was built for a thermal printer driven directly, where 32
 * fixed-width columns is all the hardware offers. Driven through Windows the
 * printer is a graphics device: it can set type. And the old output showed why
 * that matters — every line the same small size, so the shop's name, an item
 * and the total all carried equal weight, and a customer holding it at arm's
 * length could read none of them.
 *
 * So: one proportional face, and a size for each job. The name at the top and
 * the total at the bottom are what people look for, and they are what this
 * makes big.
 *
 * Millimetres throughout, because the constraint is a 48mm print head, not a
 * screen. The ESC/POS path is untouched — a printer driven directly still gets
 * the 32-column text, which is the only thing it can take.
 */
function receiptDocument(receipt: ReceiptPayload): string {
  const printableMm = receipt.paperWidthMm === 58 ? 48 : 72;
  const b = receipt.business;
  const esc = escapeHtml;

  /*
   * The header is whatever the shop typed in Settings, falling back to the
   * address on file. Typed wins, because "Artisan Breads • Pastries • Coffee"
   * and the opening hours are not things an address book can produce, and they
   * are most of what makes a receipt look like it came from somewhere.
   */
  const header = receipt.headerLines.length > 0
    ? receipt.headerLines
    : [
        b.legalName && b.legalName !== b.tradingName ? `Owned and operated by ${b.legalName}` : '',
        [b.addressLine1, b.addressLine2].filter(Boolean).join(', '),
        [b.city, b.province].filter(Boolean).join(', '),
        b.phone,
      ].filter(Boolean);

  const rule = '<div class="rule"></div>';

  const items = receipt.lines
    .map((line) => {
      const extras = [
        ...line.modifiers.map((m) => `<div class="sub">+ ${esc(m)}</div>`),
        line.notes ? `<div class="sub note">${esc(line.notes)}</div>` : '',
      ].join('');
      return `
        <div class="item">
          <div class="row">
            <span class="name">${esc(line.name)}</span>
            <span class="amt">${esc(line.lineTotal)}</span>
          </div>
          <div class="sub">${line.qty} × ${esc(line.unitPrice)}</div>
          ${extras}
        </div>`;
    })
    .join('');

  const totals = receipt.totals
    .map(
      (total) => `
        <div class="row ${total.emphasis ? 'grand' : 'total'}">
          <span>${esc(total.label)}</span>
          <span>${esc(total.value)}</span>
        </div>`,
    )
    .join('');

  const payments = receipt.payments
    .map(
      (payment) => `
        <div class="row total">
          <span>${esc(payment.method)}</span>
          <span>${esc(payment.amount)}</span>
        </div>`,
    )
    .join('');

  /*
   * How it is leaving, and where it is going.
   *
   * On a delivery this is the most useful thing on the slip: it rides with the
   * order, and whoever carries it needs the address without opening a phone.
   * Printed in its own right rather than folded into the customer line, so it
   * survives being read at arm's length in a doorway.
   */
  const dispatch = receipt.fulfillment === 'delivery' ? 'DELIVERY' : 'PICK-UP';
  const when = receipt.dueAt
    ? new Date(receipt.dueAt).toLocaleString('en-PH', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;

  const who = [
    receipt.showCashier && receipt.cashier ? `<div>Served by: ${esc(receipt.cashier)}</div>` : '',
    receipt.showCustomer && receipt.customer ? `<div><b>${esc(receipt.customer)}</b></div>` : '',
    // The address is the delivery instruction, so it prints whenever there is
    // one — a pick-up order rarely carries one at all.
    receipt.showCustomer && receipt.customerAddress
      ? `<div class="addr">${esc(receipt.customerAddress)}</div>`
      : '',
    receipt.showCustomer && receipt.customerPhone ? `<div>${esc(receipt.customerPhone)}</div>` : '',
    receipt.fulfillment === 'delivery' && receipt.riderName
      ? `<div>Rider: ${esc(receipt.riderName)}</div>`
      : '',
  ].join('');

  return `
    <style>
      /* No page margin: the head's own dead zone either side is the margin. */
      @page { size: ${printableMm}mm auto; margin: 0; }
      * { box-sizing: border-box; }
      body {
        font-family: Arial, Helvetica, system-ui, sans-serif;
        width: ${printableMm}mm;
        margin: 0;
        padding: 0 1mm;
        color: #000;
        /*
         * A thermal head is one bit deep: a dot is burned or it is not.
         * Anti-aliased type hands the driver grey edge pixels, which it
         * dithers into a scatter of dots — and that is what makes small print
         * on these rolls look soft and broken up. Turning smoothing off and
         * asking for geometric rendering keeps strokes solid, and the slight
         * extra weight on body text gives the head more to burn.
         */
        -webkit-font-smoothing: none;
        text-rendering: geometricPrecision;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      /*
       * Thermal paper has no greys. A photograph or a soft logo dithers into
       * a smudge, so the image is pushed to hard black and white before it
       * ever reaches the driver: high contrast and no half-tones.
       */
      .logo { text-align: center; margin-bottom: 1.2mm; }
      .logo img { max-width: 34mm; max-height: 22mm; filter: grayscale(1) contrast(4); }
      .shop { font-size: 5mm; font-weight: bold; text-align: center; letter-spacing: .2mm; line-height: 1.15; margin-bottom: .8mm; }
      .head { font-size: 2.6mm; font-weight: 500; text-align: center; line-height: 1.35; }
      .rule { border-top: .35mm dotted #000; margin: 1.6mm 0; }
      .meta { font-size: 2.8mm; font-weight: 500; line-height: 1.45; }
      /* Reversed out, because this is the one line a driver looks for. */
      .dispatch { font-size: 3.4mm; font-weight: bold; text-align: center; background: #000; color: #fff; padding: .7mm 0; margin-bottom: 1.2mm; letter-spacing: .3mm; }
      .addr { white-space: pre-line; }
      .item { margin-bottom: 1.4mm; }
      .row { display: flex; justify-content: space-between; align-items: baseline; gap: 1.5mm; }
      .name { font-size: 3.2mm; font-weight: bold; line-height: 1.25; }
      .amt { font-size: 3.2mm; font-weight: bold; white-space: nowrap; }
      .sub { font-size: 2.6mm; font-weight: 500; line-height: 1.3; }
      .note { font-style: italic; }
      .total { font-size: 3mm; font-weight: 500; line-height: 1.5; }
      .grand { font-size: 4.8mm; font-weight: bold; line-height: 1.4; margin: .6mm 0; }
      .foot { font-size: 2.6mm; font-weight: 500; text-align: center; line-height: 1.4; }
      .refs { font-size: 2.4mm; display: flex; justify-content: space-between; margin-top: 1.2mm; }
      /* A little roll past the last line so the tear-off misses the text. */
      .tail { height: 6mm; }
    </style>
    <body>
      ${receipt.logoUrl ? `<div class="logo"><img src="${esc(receipt.logoUrl)}" alt=""></div>` : ''}
      <div class="shop">${esc(b.tradingName)}</div>
      <div class="head">${header.map((l) => `<div>${esc(l)}</div>`).join('')}</div>

      ${rule}
      <div class="dispatch">${dispatch}${when ? ` · ${esc(when)}` : ''}</div>
      ${who ? `<div class="meta">${who}</div>` : ''}

      ${rule}
      ${items}

      ${rule}
      ${totals}
      ${receipt.payments.length > 0 ? payments : ''}
      ${receipt.changeDue ? `<div class="row total"><span>Change</span><span>${esc(receipt.changeDue)}</span></div>` : ''}

      ${receipt.footerLines.length > 0 ? `${rule}<div class="foot">${receipt.footerLines.map((l) => `<div>${esc(l)}</div>`).join('')}</div>` : ''}

      <div class="refs"><span>${esc(receipt.receiptNo)}</span><span>${esc(receipt.orderNo)}</span></div>
      <div class="tail"></div>
    </body>
  `;
}

/* Escaping lives in shared now: one copy, two builders. Re-exported here so
 * the printing module stays the one place a document builder has to import. */
export { escapeHtml };

/**
 * Open a hidden iframe, print it, and clean up.
 * An iframe rather than window.open, because pop-up blockers eat the latter and
 * a blocked receipt is a lost receipt.
 */
export function openPrintWindow(html: string, title: string): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(frame);

  const doc = frame.contentDocument;
  if (!doc) {
    document.body.removeChild(frame);
    return;
  }

  doc.open();
  doc.write(`<!doctype html><html><head><title>${escapeHtml(title)}</title></head>${html}</html>`);
  doc.close();

  const run = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    // Give the print dialog time to take its snapshot before the frame goes.
    setTimeout(() => frame.parentNode && document.body.removeChild(frame), 1000);
  };

  if (frame.contentWindow?.document.readyState === 'complete') run();
  else frame.onload = run;
}

// --- Payslips --------------------------------------------------------------

export interface PayslipPrintData {
  payslipNo?: string;
  business: { tradingName: string; legalName: string; tin: string; addressLine1: string; addressLine2: string | null; city: string; province: string; postalCode: string; vatRegistered: boolean };
  employeeName: string;
  /** Their job title, printed under the name. */
  position?: string | null;
  issuedOn: string;
  periodStart: string;
  periodEnd: string;
  lines: {
    label: string;
    unitLabel: string;
    qty: number;
    rate: number;
    amount: number;
    breakdown: { date: string; qty: number }[];
    kind?: 'earning' | 'holiday' | 'absence';
    note?: string | null;
  }[];
  adjustments: { label: string; amount: number; kind: 'addition' | 'deduction' }[];
  subtotal: number;
  grandTotal: number;
}

const money = (centavos: number) => (centavos / 100).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (value: number) => value.toLocaleString('en-PH', { maximumFractionDigits: 3 });
const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-PH', { day: 'numeric', month: 'short', timeZone: 'UTC' });

const longDate = (iso: string) =>
  new Date(iso.length > 10 ? iso : `${iso}T00:00:00Z`).toLocaleDateString('en-PH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: iso.length > 10 ? undefined : 'UTC',
  });

/**
 * The shop's wage payslip, for anyone paid by the day, the shift or the month.
 *
 * Set out in the blocks the shop's own slip uses — earnings, absences, holiday
 * pay, then net pay and an acknowledgement — rather than the piece-rate grid,
 * which has nothing to put in its columns when the answer is "eleven days".
 *
 * Returns the HTML rather than printing it, so the layout can be checked
 * without a browser. What goes on a payslip is not something to verify by eye
 * once and hope.
 */
export function buildWageSlipHtml(data: PayslipPrintData): string {
  const { business } = data;

  const earnings = data.lines.filter((line) => line.kind !== 'absence' && line.kind !== 'holiday');
  const holiday = data.lines.filter((line) => line.kind === 'holiday');
  const absences = data.lines.filter((line) => line.kind === 'absence');

  const grossPay = sumOf(earnings.map((line) => line.amount));
  const additions = data.adjustments.filter((a) => a.kind === 'addition');
  const deductions = data.adjustments.filter((a) => a.kind === 'deduction');

  /** A block with its own shaded heading, as on the paper slip. */
  const section = (title: string, rows: string, total?: number) => `
    <tr class="band"><th colspan="3">${escapeHtml(title)}</th></tr>
    ${rows}
    ${total === undefined ? '' : `<tr><td class="strong" colspan="2"></td><td class="num strong">₱${money(total)}</td></tr>`}
  `;

  const rateRow = (label: string, qty: string, amount: string, strong = false) => `
    <tr>
      <th class="row-label${strong ? ' strong' : ''}">${escapeHtml(label)}</th>
      <td class="num${strong ? ' strong' : ''}">${qty}</td>
      <td class="num${strong ? ' strong' : ''}">${amount}</td>
    </tr>
  `;

  const earningRows = earnings
    .map((line) =>
      rateRow(line.label, `${line.qty} ${escapeHtml(line.unitLabel)}`, `₱${money(line.rate)}`),
    )
    .join('');

  const holidayRows =
    holiday.length > 0
      ? holiday
          .map((line) => rateRow(line.label, `${line.qty} ${escapeHtml(line.unitLabel)}`, `₱${money(line.amount)}`))
          .join('')
      : rateRow('Regular Holidays', '0 days', '₱0.00') + rateRow('Special Non-Wk Days', '0 days', '₱0.00');

  const absenceRows =
    absences.length > 0
      ? absences
          .map(
            (line) =>
              rateRow('Shifts Absent', `${line.qty} ${escapeHtml(line.unitLabel)}`, '₱0.00') +
              `<tr><th class="row-label">Reason</th><td class="num" colspan="2">${escapeHtml(line.note ?? 'n/a')}</td></tr>`,
          )
          .join('')
      : rateRow('Shifts Absent', '0 days', '₱0.00') +
        '<tr><th class="row-label">Reason</th><td class="num" colspan="2">n/a</td></tr>';

  const adjustmentRows = (rows: typeof data.adjustments) =>
    rows.map((a) => rateRow(a.label, '', `₱${money(a.amount)}`)).join('');

  const html = `
    <style>
      @page { size: A4; margin: 16mm; }
      body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; margin: 0; }
      h1 { font-size: 12pt; margin: 0; text-align: center; }
      .sub { font-size: 10pt; margin: 2px 0 0; text-align: center; }
      .meta { font-size: 9.5pt; margin: 2px 0 0; text-align: center; line-height: 1.5; }
      .title { text-align: center; font-weight: bold; letter-spacing: .16em; margin: 20px 0 16px; }
      .who { font-size: 10pt; margin: 0 0 14px; }
      .who td { padding: 2px 0; border: none; }
      .who .k { font-weight: bold; width: 150px; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #999; padding: 4px 8px; font-size: 10pt; }
      .band th { background: #c9c9c9; text-align: center; letter-spacing: .08em; font-size: 9.5pt; }
      .row-label { text-align: left; font-weight: normal; }
      .num { text-align: center; font-variant-numeric: tabular-nums; }
      .strong { font-weight: bold; background: #f2f2f2; }
      .net th, .net td { background: #c9c9c9; font-weight: bold; font-size: 11pt; }
      .ack { margin-top: 26px; text-align: center; }
      .ack .h { font-weight: bold; letter-spacing: .05em; font-size: 10pt; }
      .ack .i { font-style: italic; font-size: 9.5pt; margin-top: 2px; }
      .sign { margin-top: 22px; font-size: 10pt; }
      .sign .line { margin-top: 30px; border-top: 1px solid #000; width: 250px; }
      .sign .f { margin-top: 18px; font-weight: bold; }
      .sign .v { color: #555; }
      .foot { margin-top: 30px; text-align: center; font-weight: bold; font-style: italic; font-size: 10pt; }
    </style>
    <body>
      <h1>${escapeHtml(business.tradingName.toUpperCase())}</h1>
      <p class="sub">Owned and Operated by ${escapeHtml(business.legalName)}</p>
      <p class="meta">
        ${business.vatRegistered ? 'VAT' : 'Non-VAT'} Reg. TIN: ${escapeHtml(business.tin)}<br />
        ${escapeHtml(business.addressLine1)}${business.addressLine2 ? `, ${escapeHtml(business.addressLine2)}` : ''},
        ${escapeHtml(business.city)}, ${escapeHtml(business.province)} ${escapeHtml(business.postalCode)}
      </p>

      <p class="title">PAYSLIP</p>

      <table class="who">
        <tr><td class="k">Date:</td><td>${escapeHtml(longDate(data.issuedOn))}</td></tr>
        <tr><td class="k">Employee Name:</td><td><strong>${escapeHtml(data.employeeName)}</strong></td></tr>
        ${data.position ? `<tr><td class="k">Position:</td><td>${escapeHtml(data.position)}</td></tr>` : ''}
        <tr><td class="k">Pay Period:</td><td>${escapeHtml(longDate(data.periodStart))} — ${escapeHtml(longDate(data.periodEnd))}</td></tr>
        ${data.payslipNo ? `<tr><td class="k">Payslip No:</td><td>${escapeHtml(data.payslipNo)}</td></tr>` : ''}
      </table>

      <table>
        ${section('EARNINGS', earningRows + rateRow('Gross Pay', '', `₱${money(grossPay)}`, true))}
        ${section('ABSENCES', absenceRows, 0)}
        ${section('HOLIDAY PAY', holidayRows, sumOf(holiday.map((line) => line.amount)))}
        ${additions.length > 0 ? section('ADDITIONS', adjustmentRows(additions), sumOf(additions.map((a) => a.amount))) : ''}
        ${deductions.length > 0 ? section('DEDUCTIONS', adjustmentRows(deductions), sumOf(deductions.map((a) => a.amount))) : ''}
        <tr class="net"><th class="row-label">NET PAY</th><td class="num" colspan="2">₱${money(data.grandTotal)}</td></tr>
      </table>

      <div class="ack">
        <div class="h">ACKNOWLEDGEMENT &amp; CERTIFICATION</div>
        <div class="i">I acknowledge receipt of the above-stated net pay amount.</div>
      </div>

      <div class="sign">
        <div class="f">Employee Signature:</div>
        <div class="line"></div>
        <div class="f">Print Name:</div>
        <div class="v">${escapeHtml(data.employeeName)}</div>
        <div class="f">Date:</div>
        <div class="v">${escapeHtml(longDate(data.issuedOn))}</div>
      </div>

      <p class="foot">FOR ACCOUNTING &amp; BIR RECORDS</p>
    </body>
  `;

  return html;
}

function printWageSlip(data: PayslipPrintData): void {
  openPrintWindow(buildWageSlipHtml(data), `Payslip — ${data.employeeName}`);
}

const sumOf = (values: number[]) => values.reduce((total, value) => total + value, 0);

/**
 * Reproduces the bakery's existing paper payslip: the header block, a column per
 * pay item with the daily counts down the side, the fee totals, then any
 * additions, the grand total and a signature line.
 */
export function printPayslip(data: PayslipPrintData): void {
  /*
   * Two slips, one function.
   *
   * The piece-rate slip is a grid: a column per rate with the day's counts
   * down the side, which is how the bakery has always paid its bakers. A
   * wage slip has nothing to put in that grid — one line reading "11 days" —
   * so it is set out the way the shop's own wage payslip is, in blocks:
   * earnings, absences, holiday pay, then net pay and a signature.
   */
  if (data.lines.some((line) => line.kind)) return printWageSlip(data);

  const { business } = data;

  // Union of every date across all pay items, so the grid has one row per day.
  const dates = [...new Set(data.lines.flatMap((line) => line.breakdown.map((entry) => entry.date)))].sort();

  const gridHeader = data.lines
    .map((line) => `<th class="num">${escapeHtml(line.label)}<span class="rate">₱${money(line.rate)} / ${escapeHtml(line.unitLabel.replace(/s$/, ''))}</span></th>`)
    .join('');

  const gridRows = dates
    .map((date) => {
      const cells = data.lines
        .map((line) => {
          const entry = line.breakdown.find((item) => item.date === date);
          return `<td class="num">${entry ? entry.qty : ''}</td>`;
        })
        .join('');
      return `<tr><th class="day">${shortDate(date)}</th>${cells}</tr>`;
    })
    .join('');

  const totalsRow = data.lines.map((line) => `<td class="num strong">${line.qty}</td>`).join('');
  const feesRow = data.lines.map((line) => `<td class="num strong">${money(line.amount)}</td>`).join('');

  const adjustmentRows = data.adjustments
    .map(
      (adjustment) =>
        `<tr><th class="day">${escapeHtml(adjustment.label)}:</th><td class="num strong" colspan="${Math.max(data.lines.length, 1)}">${
          adjustment.kind === 'deduction' ? '-' : ''
        }${money(adjustment.amount)}</td></tr>`,
    )
    .join('');

  const html = `
    <style>
      @page { size: A4; margin: 16mm; }
      body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; margin: 0; }
      h1 { font-size: 13pt; margin: 0; letter-spacing: .01em; }
      .sub { font-size: 10pt; font-weight: bold; margin: 2px 0 0; }
      .meta { font-size: 9.5pt; margin: 2px 0 0; line-height: 1.4; }
      .title { text-align: center; font-weight: bold; letter-spacing: .18em; margin: 26px 0 18px; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #000; padding: 4px 7px; font-size: 10pt; }
      thead th { background: #f0ece4; text-align: center; }
      .num { text-align: center; font-variant-numeric: tabular-nums; }
      .day { text-align: right; width: 128px; font-weight: normal; }
      .strong { font-weight: bold; background: #f7f4ee; }
      .rate { display: block; font-weight: normal; font-size: 8.5pt; color: #444; }
      .grand td, .grand th { background: #ece5d8; font-weight: bold; font-size: 11pt; }
      .head-row th { text-align: left; }
      .sign { margin-top: 54px; font-size: 10pt; }
      .sign .line { margin-top: 34px; border-top: 1px solid #000; width: 260px; padding-top: 4px; }
      .no-border td, .no-border th { border: none; }
    </style>
    <body>
      <h1>${escapeHtml(business.tradingName.toUpperCase())}</h1>
      <p class="sub">Owned and Operated by ${escapeHtml(business.legalName)}</p>
      <p class="meta">
        ${business.vatRegistered ? 'VAT' : 'Non-VAT'} Reg. TIN: ${escapeHtml(business.tin)}<br />
        ${escapeHtml(business.addressLine1)}${business.addressLine2 ? `, ${escapeHtml(business.addressLine2)}` : ''},
        ${escapeHtml(business.city)}, ${escapeHtml(business.province)} ${escapeHtml(business.postalCode)}
      </p>

      <p class="title">PAYSLIP</p>

      <table>
        <tr class="head-row">
          <th>Date: ${escapeHtml(new Date(data.issuedOn).toLocaleDateString('en-PH'))}</th>
          <th colspan="${Math.max(data.lines.length, 1)}">Name: ${escapeHtml(data.employeeName)}</th>
        </tr>
        <thead>
          <tr><th class="day">Period ${shortDate(data.periodStart)} – ${shortDate(data.periodEnd)}</th>${gridHeader}</tr>
        </thead>
        <tbody>
          ${gridRows}
          <tr><th class="day strong">Total goods:</th>${totalsRow}</tr>
          <tr><th class="day strong">Fee total:</th>${feesRow}</tr>
          ${adjustmentRows}
          <tr class="grand"><th class="day">Grand total:</th><td class="num" colspan="${Math.max(data.lines.length, 1)}">${money(data.grandTotal)}</td></tr>
        </tbody>
      </table>

      <div class="sign">
        Received by:
        <div class="line">${escapeHtml(data.employeeName)}</div>
      </div>
    </body>
  `;

  openPrintWindow(html, `Payslip — ${data.employeeName}`);
}

/**
 * The rider's round, as a sheet of paper.
 *
 * Built as a string rather than printed from the screen because the screen is a
 * grid of editable boxes and the paper is a list to tick off at a door. Grouped
 * under community headings instead of repeating the village on every line,
 * which is how the shop wrote it out by hand and how it reads from a motorbike.
 *
 * Exported so the paper can be checked by a test rather than by looking at one
 * print preview and hoping — the same reason `buildWageSlipHtml` is.
 */
export function buildDeliveryRouteHtml(day: DeliveryRouteDay): string {
  const heading = new Date(`${day.date}T12:00:00Z`).toLocaleDateString('en-PH', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  /*
   * Grouped in the order the stops are already in, never re-sorted. Somebody
   * arranged this round; re-grouping it here would quietly drive them in a
   * different order from the one they chose on screen.
   */
  const groups: { community: string; stops: DeliveryStop[] }[] = [];
  for (const stop of day.stops) {
    const name = stop.community || 'Elsewhere';
    const last = groups.at(-1);
    if (last && last.community === name) last.stops.push(stop);
    else groups.push({ community: name, stops: [stop] });
  }


  const rows = groups
    .map(
      (group) => `
        <tr class="band"><th colspan="7">${escapeHtml(group.community)}</th></tr>
        ${group.stops
          .map(
            (stop) => `
              <tr>
                <td class="num">${stop.position}</td>
                <td class="tick"></td>
                <td>
                  <span class="who">${escapeHtml(stop.customerName)}</span>
                  <span class="meta">${escapeHtml(stop.orderNo)}</span>
                </td>
                <td>${
                  stop.addressLine
                    ? escapeHtml(stop.addressLine)
                    : '<span class="missing">no address on file</span>'
                }</td>
                <td class="nowrap">${escapeHtml(stop.contact)}</td>
                <td class="num">${stop.itemCount}</td>
                <td class="num">${stop.balanceDue > 0 ? `₱${money(stop.balanceDue)}` : 'Paid'}</td>
              </tr>
              ${
                stop.notes
                  ? `<tr class="note"><td></td><td></td><td colspan="5">${escapeHtml(stop.notes)}</td></tr>`
                  : ''
              }
            `,
          )
          .join('')}
      `,
    )
    .join('');

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Delivery route — ${escapeHtml(heading)}</title>
    <style>
      @page { size: A4 portrait; margin: 12mm; }
      * { box-sizing: border-box; }
      body { font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; color: #111; margin: 0; }
      h1 { font-size: 16pt; margin: 0; }
      .sub { font-size: 9pt; color: #555; margin: 2mm 0 4mm; }
      table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
      th, td { border-bottom: 0.4pt solid #bbb; padding: 1.6mm 1.4mm; text-align: left; vertical-align: top; }
      thead th { border-bottom: 0.8pt solid #333; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.04em; }
      /* Repeated on a page break, so a second page is still readable. */
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      .band th { background: #eee; font-size: 9pt; padding: 1.4mm; }
      .num { text-align: right; white-space: nowrap; }
      .nowrap { white-space: nowrap; }
      .who { display: block; font-weight: 600; }
      .meta { color: #666; font-size: 8pt; }
      .missing { color: #a33; font-style: italic; }
      .note td { color: #333; font-size: 8.5pt; font-style: italic; padding-top: 0; }
      /* A box to tick at the door: the copy that comes back is the record. */
      .tick { width: 7mm; }
      .tick::after { content: ''; display: block; width: 4.5mm; height: 4.5mm; border: 0.6pt solid #333; }
      tfoot td { border-bottom: 0; font-weight: 600; padding-top: 2.5mm; }
      .sign { margin-top: 10mm; font-size: 9pt; color: #555; }
      .sign span { border-top: 0.4pt solid #777; display: inline-block; margin-right: 12mm; padding-top: 1.5mm; width: 55mm; }
    </style>
  </head>
  <body>
    <h1>Delivery route</h1>
    <p class="sub">
      ${escapeHtml(heading)} &middot; ${plural(day.stops.length, 'stop', 'stops')}
      &middot; ${plural(day.itemCount, 'item', 'items')}
      ${day.balanceDue > 0 ? `&middot; ₱${money(day.balanceDue)} to collect` : ''}
    </p>

    <table>
      <thead>
        <tr>
          <th class="num">#</th>
          <th></th>
          <th>Name</th>
          <th>Address</th>
          <th>Contact #</th>
          <th class="num">Items</th>
          <th class="num">To collect</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr>
          <td colspan="5">Total</td>
          <td class="num">${day.itemCount}</td>
          <td class="num">${day.balanceDue > 0 ? `₱${money(day.balanceDue)}` : '—'}</td>
        </tr>
      </tfoot>
    </table>

    <p class="sign"><span>Rider</span><span>Cash returned</span></p>
  </body>
</html>`;
}

export function printDeliveryRoute(day: DeliveryRouteDay): void {
  openPrintWindow(buildDeliveryRouteHtml(day), `Delivery route ${day.date}`);
}

// --- Daily report ----------------------------------------------------------

/**
 * A month of trading, one row a day, for the accountant or the folder.
 *
 * Landscape, because eleven columns on A4 portrait leaves them unreadable.
 * The closed days are kept rather than filtered out: a gap in the dates makes
 * the reader wonder what was left out, where a row marked "closed" answers it.
 */
function buildDailyReportHtml(report: DailyReport, heading: string): string {
  const cell = (centavos: number, soft = false) =>
    centavos === 0 && soft ? '<td class="num dim">—</td>' : `<td class="num">${money(centavos)}</td>`;

  const rows = report.rows
    .map((row) => {
      const date = new Date(`${row.date}T12:00:00Z`);
      const day = date.toLocaleDateString('en-PH', { weekday: 'short' });
      const num = date.toLocaleDateString('en-PH', { day: 'numeric', month: 'short' });

      return `
        <tr class="${row.isOpenDay ? '' : 'shut'}">
          <td class="nowrap">${escapeHtml(num)} <span class="meta">${escapeHtml(day)}</span></td>
          <td class="num">${row.isOpenDay ? row.orderCount || '—' : '<span class="meta">closed</span>'}</td>
          <td class="num">${row.itemCount || '—'}</td>
          ${cell(row.sales, true)}
          ${cell(row.deliveryFees, true)}
          ${cell(row.costOfGoods, true)}
          ${cell(row.grossProfit, true)}
          ${cell(row.overheads, true)}
          ${cell(row.wages, true)}
          ${cell(row.oneOffs, true)}
          <td class="num strong ${row.netProfit < 0 ? 'down' : ''}">${row.netProfit === 0 ? '—' : money(row.netProfit)}</td>
        </tr>`;
    })
    .join('');

  const totals = report.totals;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Daily report — ${escapeHtml(heading)}</title>
    <style>
      @page { size: A4 landscape; margin: 10mm; }
      * { box-sizing: border-box; }
      body { font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; color: #111; margin: 0; }
      h1 { font-size: 15pt; margin: 0; }
      .sub { font-size: 8.5pt; color: #555; margin: 1.5mm 0 3.5mm; }
      table { width: 100%; border-collapse: collapse; font-size: 8pt; }
      th, td { border-bottom: 0.3pt solid #ccc; padding: 1.1mm 1.2mm; text-align: left; }
      thead th { border-bottom: 0.8pt solid #333; font-size: 7pt; text-transform: uppercase; letter-spacing: 0.03em; }
      /* Repeated on a page break, because a month rarely fits one page. */
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      .num { text-align: right; white-space: nowrap; }
      .nowrap { white-space: nowrap; }
      .meta { color: #777; font-size: 7pt; }
      .dim { color: #aaa; }
      .strong { font-weight: 600; }
      .down { color: #a33; }
      /* A closed day is greyed rather than dropped, so no date is missing. */
      .shut td { background: #f6f6f6; color: #666; }
      tfoot td { border-bottom: 0; border-top: 0.8pt solid #333; font-weight: 700; padding-top: 2mm; }
      .note { color: #555; font-size: 7.5pt; margin-top: 5mm; line-height: 1.5; }
    </style>
  </head>
  <body>
    <h1>Daily sales and profit</h1>
    <p class="sub">
      ${escapeHtml(heading)} &middot; ${plural(report.tradingDays, 'trading day', 'trading days')}
      &middot; ₱${money(totals.sales)} taken &middot; ₱${money(totals.netProfit)} kept
    </p>

    <table>
      <thead>
        <tr>
          <th>Day</th>
          <th class="num">Orders</th>
          <th class="num">Items</th>
          <th class="num">Sales</th>
          <th class="num">Delivery</th>
          <th class="num">Ingredients</th>
          <th class="num">Gross</th>
          <th class="num">Overheads</th>
          <th class="num">Wages</th>
          <th class="num">One-offs</th>
          <th class="num">Net</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr>
          <td>Month</td>
          <td class="num">${totals.orderCount}</td>
          <td class="num">${totals.itemCount}</td>
          <td class="num">${money(totals.sales)}</td>
          <td class="num">${money(totals.deliveryFees)}</td>
          <td class="num">${money(totals.costOfGoods)}</td>
          <td class="num">${money(totals.grossProfit)}</td>
          <td class="num">${money(totals.overheads)}</td>
          <td class="num">${money(totals.wages)}</td>
          <td class="num">${money(totals.oneOffs)}</td>
          <td class="num ${totals.netProfit < 0 ? 'down' : ''}">${money(totals.netProfit)}</td>
        </tr>
      </tfoot>
    </table>

    <p class="note">
      Rent, bills and the wage bill do not arrive daily, so each is spread over the days it
      buys — the standing costs across the month's ${plural(report.tradingDays, 'trading day', 'trading days')},
      and each payslip across the days it covers. Nothing is charged to a day the shop was shut.
      Ingredients are what the goods sold that day cost to make, read from the recipes, so the
      month's deliveries are not counted a second time.
    </p>
  </body>
</html>`;
}

export function printDailyReport(report: DailyReport, heading: string): void {
  openPrintWindow(buildDailyReportHtml(report, heading), `Daily report ${report.month}`);
}

// --- Stock history ---------------------------------------------------------

/**
 * What came in over a month, for the folder or the bookkeeper.
 *
 * Portrait: four columns fit comfortably, and this is read down a day at a
 * time rather than across. Each day keeps its own total, because the question
 * behind the sheet is usually "what did we spend on the eighth".
 */
function buildStockHistoryHtml(history: StockHistory, heading: string): string {
  const days = history.days
    .map((day) => {
      const title = new Date(`${day.date}T12:00:00Z`).toLocaleDateString('en-PH', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      });

      const rows = history.days.length === 0 ? '' : day.lines
        .map((line) => {
          const what = [line.supplier, line.reference, line.note].filter(Boolean).join(' &middot; ');
          const tag =
            line.kind === 'delivery' ? '' : ` <span class="tag">${line.kind === 'count' ? 'counted' : 'written off'}</span>`;

          return `
            <tr${line.kind === 'delivery' ? '' : ' class="corr"'}>
              <td>
                ${escapeHtml(line.ingredientName)}${tag}
                ${what ? `<span class="meta">${what}</span>` : ''}
              </td>
              <td class="num">${line.qty > 0 && line.kind !== 'delivery' ? '+' : ''}${qty(line.qty)} ${escapeHtml(line.unit)}</td>
              <td class="num">${line.unitPrice != null ? `₱${money(line.unitPrice)}` : '&mdash;'}</td>
              <td class="num strong">${
                line.lineTotal != null
                  ? `₱${money(line.lineTotal)}`
                  : line.kind === 'delivery'
                    ? '<span class="dim">not priced</span>'
                    : '<span class="dim">&mdash;</span>'
              }</td>
            </tr>`;
        })
        .join('');

      return `
        <tr class="band">
          <th colspan="3">${escapeHtml(title)}</th>
          <th class="num">${day.spent > 0 ? `₱${money(day.spent)}` : '&mdash;'}</th>
        </tr>
        ${rows}`;
    })
    .join('');

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>What came in — ${escapeHtml(heading)}</title>
    <style>
      @page { size: A4 portrait; margin: 12mm; }
      * { box-sizing: border-box; }
      body { font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; color: #111; margin: 0; }
      h1 { font-size: 16pt; margin: 0; }
      .sub { font-size: 9pt; color: #555; margin: 2mm 0 4mm; }
      table { width: 100%; border-collapse: collapse; font-size: 9.5pt; }
      th, td { border-bottom: 0.4pt solid #ccc; padding: 1.5mm 1.4mm; text-align: left; vertical-align: top; }
      thead th { border-bottom: 0.8pt solid #333; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.04em; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      .band th { background: #eee; font-size: 9.5pt; padding: 2mm 1.4mm; border-top: 0.6pt solid #999; }
      .num { text-align: right; white-space: nowrap; }
      .strong { font-weight: 600; }
      .dim { color: #999; font-weight: 400; }
      .meta { display: block; color: #666; font-size: 8pt; }
      /* A correction is not a purchase; it reads as a footnote to the day. */
      .corr td { color: #555; }
      .tag { font-size: 7.5pt; color: #555; border: 0.4pt solid #bbb; border-radius: 2pt; padding: 0 1mm; }
      tfoot td { border-bottom: 0; border-top: 0.8pt solid #333; font-weight: 700; padding-top: 2.5mm; }
      .note { color: #555; font-size: 8pt; margin-top: 6mm; line-height: 1.5; }
    </style>
  </head>
  <body>
    <h1>What came in</h1>
    <p class="sub">
      ${escapeHtml(heading)} &middot; ${plural(history.deliveries, 'delivery', 'deliveries')}
      &middot; ₱${money(history.spent)} spent
      ${history.corrections > 0 ? `&middot; ${plural(history.corrections, 'correction', 'corrections')}` : ''}
    </p>

    <table>
      <thead>
        <tr>
          <th>Item</th>
          <th class="num">Quantity</th>
          <th class="num">Unit price</th>
          <th class="num">Cost</th>
        </tr>
      </thead>
      <tbody>${days}</tbody>
      <tfoot>
        <tr>
          <td colspan="3">Month</td>
          <td class="num">₱${money(history.spent)}</td>
        </tr>
      </tfoot>
    </table>

    <p class="note">
      Deliveries are what was bought and received, priced as invoiced. Counts and write-offs
      move stock without money changing hands, so they carry no cost. A delivery recorded
      before its invoice arrived shows as not priced rather than as free.
    </p>
  </body>
</html>`;
}

export function printStockHistory(history: StockHistory, heading: string): void {
  openPrintWindow(buildStockHistoryHtml(history, heading), `What came in ${history.month}`);
}
